import { Form, Formik } from "formik";
import React, { useEffect, useMemo } from "react";
import {
  AlertComponent,
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import {
  ORDER_STATUS,
  orderValidationSchema,
  PAYMENT_STATUS,
} from "../../order-management-helper";
import "./create-order.scss";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import dayjs from "dayjs";
import CompanySelect from "../../../../user-management/user-management-list/components/company-select/company-select";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import { getTenantDetails } from "../../../../../api/tenets";
import { createOrder } from "../../../../../api/order-management";

interface CreateOrderProps {
  setIsOpen: (isOpen: boolean) => void;
  orderId?: string;
  companyNames?: any;
}

const CreateOrder: React.FC<CreateOrderProps> = ({
  setIsOpen,
  orderId,
  companyNames,
}) => {
  const [company, setCompany] = React.useState<any>(null);
  const [data, setData] = React.useState<any>(null);
  const [userData, setUserData] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [planData, setPlanData] = React.useState<any>([]);
  const [planFrequencyData, setPlanFrequencyData] = React.useState<any>([]);
  const formikRef = React.useRef<any>(null);
  const [alert, setAlert] = React.useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const [onClose, setOnclose] = React.useState<boolean>(false);

  const initialValues = {
    orderId: orderId ?? "",
    status: "",
    tenantId: "",
    customer: "",
    paymentStatus: "",
    totalAmount: "",
    selectedPlan: "",
    planFrequency: "",
    createdOn: "",
  };

  const handleClose = () => {
    setTimeout(() => {
      setIsOpen(false);
    }, 1000);
  };

  const fetchTenantData = async (setFieldValue?: any, setTouched?: any) => {
    try {
      setIsLoading(true);

      const response = await getTenantDetails({ id: company });
      const tenantData = response.data?.data ?? [];
      setData(tenantData);

      const primaryUser = tenantData.users
        .filter((user: any) => user.is_primary_user)
        .map((user: any) => ({
          value: `${user.first_name ?? ""}${" "}${user.last_name ?? ""}`,
        }));

      const selectedPlan = tenantData.purchased_subscription_plan
        ? [{ value: tenantData?.purchased_subscription_plan?.plan_name }]
        : [];
      const planFrequency = tenantData.purchased_subscription_plan
        ? [{ value: tenantData?.purchased_subscription_plan?.plan_type }]
        : [];

      setFieldValue("customer", primaryUser[0]?.value);
      if (selectedPlan) {
        setFieldValue("selectedPlan", selectedPlan[0]?.value);
      }
      if (planFrequency) {
        setFieldValue("planFrequency", planFrequency[0]?.value);
      }

      setUserData(primaryUser);
      setPlanData(selectedPlan);
      setPlanFrequencyData(planFrequency);
      // setTouched({
      //   customer: true,
      //   selectedPlan: true,
      //   planFrequency: true,
      // });
    } catch (error: any) {
      console.error("Error fetching user profile:", error);
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to fetch tenant data",
        type: "error",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (values: any, resetForm: any) => {
    setIsLoading(true);
    try {
      const payload = {
        orderNumber: values.orderId,
        status: values.status,
        tenantId: values.tenantId,
        tenantSubscriptionPlanId: data?.purchased_subscription_plan?.id,
        paymentStatus: values.paymentStatus,
        totalAmount: values.totalAmount,
      };
      const response = await createOrder(payload);
      setAlert({
        isOpen: true,
        message: response?.data?.message || "Order created successfully",
        type: "success",
      });
      resetForm();
      if (onClose) {
        handleClose();
      }
    } catch (error: any) {
      console.error("Error saving order:", error);
      setAlert({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to create order",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (company && formikRef.current) {
      fetchTenantData(
        formikRef.current.setFieldValue,
        formikRef.current.setTouched,
      );
    } else {
      setData((prev: any) => ({
        ...prev,
        billing_address: null,
        company_address: null,
      }));
      setUserData([]);
      setPlanData([]);
      setPlanFrequencyData([]);
      formikRef.current.setFieldValue("customer", "");
      formikRef.current.setFieldValue("selectedPlan", "");
      formikRef.current.setFieldValue("planFrequency", "");
    }
  }, [company]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div className="order-create-wrapper">
      <Formik
        innerRef={formikRef}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={orderValidationSchema}
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          resetForm,
          touched,
          errors,
        }) => {
          return (
            <Form className="order-create-form">
              <div className="order-form-scrollable">
                <div className="order-form-wrapper">
                  <TextInputComponent
                    type="text"
                    fieldName="orderNumber"
                    label="Order ID"
                    placeholder="Enter order Id"
                    value={values.orderId}
                    onChange={(e: any) =>
                      setFieldValue("orderId", e?.target?.value)
                    }
                    isRequired={true}
                    isName={true}
                    minlength="3"
                    maxlength="50"
                    disabled={!!orderId}
                  />
                  <SelectInputComponent
                    fieldName="status"
                    label="Order Status"
                    placeholder="Select order status"
                    options={ORDER_STATUS}
                    onChange={(e: any) => setFieldValue("status", e)}
                    value={values.status}
                    isRequired={true}
                    isShowError={!!touched.status && !!errors.status}
                  />
                  <CompanySelect
                    value={values.tenantId}
                    onChange={(option) => {
                      setFieldValue("tenantId", option);
                      setCompany(option);
                    }}
                    companyNames={companyNames.filter(
                      (company: any) => company.isOnboardingCompleted,
                    )}
                    disabled={false}
                    touched={!!touched.tenantId}
                    error={errors.tenantId}
                    isLabelled
                    isRequired
                    borderRequired={true}
                  />
                  <SelectInputComponent
                    fieldName="planFrequency"
                    label="Customer"
                    placeholder="Select customer"
                    options={userData}
                    onChange={(e: any) => setFieldValue("customer", e)}
                    value={values.customer}
                    disabled={!company}
                    isRequired={true}
                    isShowError={!!touched.customer && !!errors.customer}
                  />
                  <SelectInputComponent
                    fieldName="paymentStatus"
                    label="Payment Status"
                    placeholder="Select payment status"
                    options={PAYMENT_STATUS}
                    onChange={(e: any) => setFieldValue("paymentStatus", e)}
                    value={values.paymentStatus}
                  />
                  <TextInputComponent
                    type="text"
                    fieldName="totalAmount"
                    label="Total Amount"
                    placeholder="Enter total amount"
                    value={values.totalAmount}
                    onChange={(e: any) =>
                      setFieldValue("totalAmount", e?.target.value)
                    }
                    isRequired={true}
                    minlength="3"
                    maxlength="50"
                    isNumber={true}
                  />
                  <SelectInputComponent
                    fieldName="planFrequency"
                    label="Select Plan"
                    placeholder="Select plan"
                    options={planData}
                    onChange={(e: any) => setFieldValue("selectedPlan", e)}
                    value={values.selectedPlan}
                    isRequired={true}
                    isShowError={
                      !!touched.selectedPlan && !!errors.selectedPlan
                    }
                  />
                  <SelectInputComponent
                    fieldName="planFrequency"
                    label="Plan Frequency"
                    placeholder="Select plan frequency"
                    options={planFrequencyData}
                    onChange={(e: any) => setFieldValue("planFrequency", e)}
                    value={values.planFrequency}
                    isRequired={true}
                    isShowError={
                      !!touched.planFrequency && !!errors.planFrequency
                    }
                  />
                  <DatePickerComponent
                    fieldName="createdOn"
                    label="Created On"
                    value={values?.createdOn ? dayjs(values.createdOn) : null}
                    onChange={(date) => setFieldValue("createdOn", date)}
                    minDate={dayjs("1900-01-01")}
                    maxDate={dayjs()}
                    optional={true}
                  />
                </div>

                {data && data?.billing_address ? (
                  <div className="order-address-wrapper">
                    <div className="order-address-header">
                      <h2>Billing Address</h2>
                    </div>
                    <div className="order-address-content">
                      <div className="order-address-info">
                        <div className="order-address-item">
                          <p className="label">Street</p>
                          <p className="value">
                            {data?.billing_address?.street_line_1 || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">City</p>
                          <p className="value">
                            {data?.billing_address?.city || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">Country</p>
                          <p className="value">
                            {data?.billing_address?.country || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="order-address-info">
                        <div className="order-address-item">
                          <p className="label">Street Line 2</p>
                          <p className="value">
                            {data?.billing_address?.street_line_2 || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">State</p>
                          <p className="value">
                            {data?.billing_address?.state || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">Zipcode</p>
                          <p className="value">
                            {data?.billing_address?.postcode || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                {data && data.company_address ? (
                  <div className="order-address-wrapper">
                    <div className="order-address-header">
                      <h2>Mailing Address</h2>
                    </div>
                    <div className="order-address-content">
                      <div className="order-address-info">
                        <div className="order-address-item">
                          <p className="label">Street</p>
                          <p className="value">
                            {data?.company_address?.street_line_1 || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">City</p>
                          <p className="value">
                            {data?.company_address?.city || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">Country</p>
                          <p className="value">
                            {data?.company_address?.country || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="order-address-info">
                        <div className="order-address-item">
                          <p className="label">Street Line 2</p>
                          <p className="value">
                            {data?.company_address?.street_line_2 || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">State</p>
                          <p className="value">
                            {data?.company_address?.state || "-"}
                          </p>
                        </div>
                        <div className="order-address-item">
                          <p className="label">Zipcode</p>
                          <p className="value">
                            {data?.company_address?.postcode || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="order-form-footer">
                <button
                  type="button"
                  className="button-outlined"
                  onClick={() => {
                    resetForm();
                    setData(null);
                    setIsOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-outlined"
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  Save & New
                </button>
                <button
                  type="button"
                  className="submit-button"
                  onClick={() => {
                    handleSubmit();
                    setOnclose(true);
                  }}
                >
                  Save
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default CreateOrder;
